import { Link } from "react-router-dom";
import { Article, CATEGORY_CONFIG } from "@/data/mockData";
import { Clock, ArrowRight, AlertTriangle, Newspaper, Building2, Landmark, Scale, Star, BarChart3, TrendingUp, Trophy, Shield } from "lucide-react";
import Sparkline from "./Sparkline";

interface ArticleCardProps {
  article: Article;
  index: number;
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  prop_firm: Building2,
  broker: Landmark,
  fintech: TrendingUp,
  regulation: Shield,
  review: Star,
  comparison: Scale,
  market_analysis: BarChart3,
  awards: Trophy,
  expose: AlertTriangle,
  event: Newspaper,
};

// Brand colors for known entities
const ENTITY_COLORS: Record<string, { bg: string; text: string }> = {
  "FTMO": { bg: "bg-[#1a1a2e]", text: "text-[#00d4aa]" },
  "FundedNext": { bg: "bg-[#0f1628]", text: "text-[#3b82f6]" },
  "The Funded Trader": { bg: "bg-[#1a0f28]", text: "text-[#a855f7]" },
  "E8 Funding": { bg: "bg-[#0f1a28]", text: "text-[#06b6d4]" },
  "MyForexFunds": { bg: "bg-[#1a1a0f]", text: "text-[#eab308]" },
  "IC Markets": { bg: "bg-[#0f1a1a]", text: "text-[#14b8a6]" },
  "Pepperstone": { bg: "bg-[#1a0f1a]", text: "text-[#e879f9]" },
  "Exness": { bg: "bg-[#1a1a0f]", text: "text-[#f59e0b]" },
  "XM Group": { bg: "bg-[#0f1628]", text: "text-[#60a5fa]" },
  "FP Markets": { bg: "bg-[#0f2818]", text: "text-[#22c55e]" },
  "AvaTrade": { bg: "bg-[#1a1428]", text: "text-[#818cf8]" },
  "OANDA": { bg: "bg-[#28180f]", text: "text-[#fb923c]" },
  "SurgeTrader": { bg: "bg-[#280f1a]", text: "text-[#f43f5e]" },
  "The 5%ers": { bg: "bg-[#0f2828]", text: "text-[#2dd4bf]" },
  "Audacity Capital": { bg: "bg-[#281a0f]", text: "text-[#d97706]" },
  "Stripe": { bg: "bg-[#1a0f28]", text: "text-[#8b5cf6]" },
  "Payoneer": { bg: "bg-[#28180f]", text: "text-[#f97316]" },
  "NTW Markets": { bg: "bg-[#280f0f]", text: "text-[#ef4444]" },
  "Trinity Capitals": { bg: "bg-[#280f0f]", text: "text-[#f87171]" },
  "Imperial Markets": { bg: "bg-[#28100f]", text: "text-[#fb923c]" },
  "Levels": { bg: "bg-[#1a0f0f]", text: "text-[#fca5a5]" },
  "TradeTIME": { bg: "bg-[#28130f]", text: "text-[#f97316]" },
  "Spec Capitals": { bg: "bg-[#250f0f]", text: "text-[#dc2626]" },
  "Entice Capital": { bg: "bg-[#260f10]", text: "text-[#e11d48]" },
  "Classic Global": { bg: "bg-[#221010]", text: "text-[#b91c1c]" },
  "Londonex": { bg: "bg-[#200f0f]", text: "text-[#f43f5e]" },
  "Landmark Markets": { bg: "bg-[#2a0a0a]", text: "text-[#dc2626]" },
  "TransX Markets": { bg: "bg-[#2a0f0a]", text: "text-[#ea580c]" },
  "PhyxTrade": { bg: "bg-[#250a0a]", text: "text-[#ef4444]" },
  "LIVAXXEN": { bg: "bg-[#280a0a]", text: "text-[#b91c1c]" },
  "Green Wall Enterprises": { bg: "bg-[#1a0a0a]", text: "text-[#dc2626]" },
  "iTrade Associates": { bg: "bg-[#220a0a]", text: "text-[#ef4444]" },
  "Jainam Stock Broking": { bg: "bg-[#1a100a]", text: "text-[#f97316]" },
  "My Forex Funds": { bg: "bg-[#1a0f0a]", text: "text-[#ea580c]" },
  "Topstep": { bg: "bg-[#0f1a28]", text: "text-[#3b82f6]" },
  "Swiss Firmup": { bg: "bg-[#0f2018]", text: "text-[#22c55e]" },
  "EverForward": { bg: "bg-[#0f1828]", text: "text-[#06b6d4]" },
  "Interactive Brokers": { bg: "bg-[#0f1a20]", text: "text-[#14b8a6]" },
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function getEntityInitials(name: string): string {
  return name.split(" ").map(w => w[0]).join("").slice(0, 3).toUpperCase();
}

// CSS-generated visual based on article category
const ArticleVisual = ({ article }: { article: Article }) => {
  const config = CATEGORY_CONFIG[article.category];
  const CategoryIcon = CATEGORY_ICONS[article.category] || Newspaper;

  // Review: star rating display
  if (article.category === "review" && article.rating) {
    const fullStars = Math.floor(article.rating);
    const entityColors = article.entityName ? ENTITY_COLORS[article.entityName] : null;
    return (
      <div className={`relative h-44 flex flex-col items-center justify-center ${entityColors?.bg || "bg-muted"}`}>
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="relative z-10 flex flex-col items-center gap-3">
          {article.entityName && (
            <span className={`font-display text-2xl font-bold ${entityColors?.text || "text-foreground"}`}>
              {getEntityInitials(article.entityName)}
            </span>
          )}
          <div className="flex items-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`h-5 w-5 ${i < fullStars ? "fill-cat-review text-cat-review" : "text-muted-foreground/30"}`}
              />
            ))}
          </div>
          <span className="font-display text-xl font-bold text-foreground">{article.rating}/5</span>
        </div>
      </div>
    );
  }

  // Comparison: VS split circle
  if (article.category === "comparison" && article.versus) {
    return (
      <div className="relative h-44 flex items-center justify-center bg-muted">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="relative z-10 flex items-center gap-4">
          <div className="flex flex-col items-center">
            <div className="h-14 w-14 rounded-full bg-secondary/20 flex items-center justify-center">
              <span className="font-display text-sm font-bold text-secondary">{getEntityInitials(article.versus[0])}</span>
            </div>
            <span className="text-xs text-muted-foreground mt-1.5 max-w-[80px] text-center truncate">{article.versus[0]}</span>
          </div>
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-cat-comparison to-cat-prop-firm flex items-center justify-center">
            <span className="font-display text-sm font-bold text-foreground">VS</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="font-display text-sm font-bold text-primary">{getEntityInitials(article.versus[1])}</span>
            </div>
            <span className="text-xs text-muted-foreground mt-1.5 max-w-[80px] text-center truncate">{article.versus[1]}</span>
          </div>
        </div>
      </div>
    );
  }

  // Awards: trophy icon
  if (article.category === "awards") {
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-gradient-to-br from-cat-awards/10 to-cat-awards/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          <div className="h-16 w-16 rounded-full bg-cat-awards/20 flex items-center justify-center">
            <Trophy className="h-8 w-8 text-cat-awards" />
          </div>
          <span className="font-display text-xs font-semibold text-cat-awards uppercase tracking-wider">Awards 2025</span>
        </div>
      </div>
    );
  }

  // Exposé: prominent warning visual
  if (article.category === "expose") {
    const entityColors = article.entityName ? ENTITY_COLORS[article.entityName] : null;
    return (
      <div className={`relative h-44 flex flex-col items-center justify-center ${entityColors?.bg || "bg-gradient-to-br from-destructive/15 to-destructive/5"}`}>
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          {article.entityName ? (
            <>
              <div className="h-14 w-14 rounded-xl flex items-center justify-center border border-destructive/30"
                style={{ background: "hsl(var(--surface-elevated))" }}>
                <span className={`font-display text-lg font-bold ${entityColors?.text || "text-destructive"}`}>
                  {getEntityInitials(article.entityName)}
                </span>
              </div>
              <span className={`font-display text-sm font-semibold ${entityColors?.text || "text-destructive"}`}>
                {article.entityName}
              </span>
            </>
          ) : (
            <div className="h-16 w-16 rounded-full bg-destructive/20 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
          )}
          <span className="font-display text-[10px] font-bold text-destructive uppercase tracking-widest">Exposé</span>
        </div>
      </div>
    );
  }

  // Regulation: warning badge
  if (article.category === "regulation") {
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-gradient-to-br from-cat-regulation/10 to-cat-expose/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          <div className="h-16 w-16 rounded-full bg-cat-regulation/20 flex items-center justify-center">
            <Shield className="h-8 w-8 text-cat-regulation" />
          </div>
          <span className="font-display text-xs font-semibold text-cat-regulation uppercase tracking-wider">Regulatory</span>
        </div>
      </div>
    );
  }

  // Market analysis: chart line
  if (article.category === "market_analysis") {
    const sparkTrend = article.headline.toLowerCase().includes("surge") || article.headline.toLowerCase().includes("rally") ? "up" as const : "flat" as const;
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-muted">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="relative z-10 flex flex-col items-center gap-2 w-full px-8">
          <BarChart3 className="h-8 w-8 text-cat-analysis mb-1" />
          <div className="w-full h-16">
            <Sparkline trend={sparkTrend} className="w-full h-full" />
          </div>
          <span className="font-display text-xs font-semibold text-cat-analysis uppercase tracking-wider">Analysis</span>
        </div>
      </div>
    );
  }

  // Broker/Prop Firm/Fintech: Entity brand block with initials
  if (article.entityName) {
    const entityColors = ENTITY_COLORS[article.entityName];
    return (
      <div className={`relative h-44 flex flex-col items-center justify-center ${entityColors?.bg || "bg-muted"}`}>
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="relative z-10 flex flex-col items-center gap-3">
          <div className={`h-16 w-16 rounded-xl flex items-center justify-center border border-border/50`}
            style={{ background: "hsl(var(--surface-elevated))" }}>
            <span className={`font-display text-xl font-bold ${entityColors?.text || "text-primary"}`}>
              {getEntityInitials(article.entityName)}
            </span>
          </div>
          <span className={`font-display text-sm font-semibold ${entityColors?.text || "text-foreground"}`}>
            {article.entityName}
          </span>
        </div>
      </div>
    );
  }

  // Default: Category icon
  return (
    <div className="relative h-44 flex flex-col items-center justify-center bg-muted">
      <div className="absolute inset-0 bg-grid-pattern opacity-20" />
      <div className="relative z-10 flex flex-col items-center gap-2">
        <div className={`h-16 w-16 rounded-full ${config.bgClass} flex items-center justify-center`}>
          <CategoryIcon className={`h-8 w-8 ${config.textClass}`} />
        </div>
        <span className={`font-display text-xs font-semibold ${config.textClass} uppercase tracking-wider`}>{config.label}</span>
      </div>
    </div>
  );
};

const ArticleCard = ({ article, index }: ArticleCardProps) => {
  const config = CATEGORY_CONFIG[article.category];
  const isHighImpact = article.impactLevel === "high";
  const CategoryIcon = CATEGORY_ICONS[article.category] || Newspaper;

  // Determine left border color
  const borderColorMap: Record<string, string> = {
    prop_firm: "border-l-cat-prop-firm",
    broker: "border-l-cat-broker",
    fintech: "border-l-cat-fintech",
    regulation: "border-l-cat-regulation",
    review: "border-l-cat-review",
    comparison: "border-l-cat-comparison",
    market_analysis: "border-l-cat-analysis",
    awards: "border-l-cat-awards",
    expose: "border-l-cat-expose",
    event: "border-l-cat-event",
  };

  return (
    <Link to={`/news/${article.slug}`} className="block">
    <article
      className={`group relative rounded-lg border-l-4 border bg-card overflow-hidden transition-colors duration-200
        hover:bg-surface-elevated cursor-pointer
        ${borderColorMap[article.category] || "border-l-primary"}
        ${isHighImpact ? "border-destructive/30 hover:border-destructive/50" : "border-border hover:border-primary/30"}
        animate-fade-in
      `}
      style={{ animationDelay: `${Math.min(index * 50, 200)}ms`, animationFillMode: 'both' }}
    >
      {isHighImpact && (
        <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-destructive to-transparent" />
      )}

      {/* CSS-generated visual */}
      <ArticleVisual article={article} />

      {/* Category badge overlay */}
      <div className="absolute top-3 left-3 z-10">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold glass ${config.textClass}`}>
          <CategoryIcon className="h-3 w-3" />
          {config.label}
        </span>
      </div>

      {isHighImpact && (
        <div className="absolute top-3 right-3 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-destructive/80 backdrop-blur-sm text-destructive-foreground">
            <AlertTriangle className="h-3 w-3" />
            High Impact
          </span>
        </div>
      )}

      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {timeAgo(article.createdAt)}
          </span>
          {article.entityName && (
            <span className="text-xs text-muted-foreground">
              · {article.entityName}
            </span>
          )}
          {article.rating && (
            <span className="flex items-center gap-0.5 text-xs text-cat-review">
              <Star className="h-3 w-3 fill-cat-review" />
              {article.rating}
            </span>
          )}
        </div>

        <h3 className="font-display text-lg font-semibold leading-tight mb-2 group-hover:text-primary transition-colors">
          {article.headline}
        </h3>

        <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">
          {article.excerpt}
        </p>

        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            <span className="text-foreground font-medium">{article.author}</span>
            {" · "}
            {article.organization}
            {article.readTime && <span> · {article.readTime} read</span>}
          </div>
          <span
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-primary transition-all"
          >
            Read Wire <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </article>
    </Link>
  );
};

export default ArticleCard;
